<template>
    <wrapper-features-templates-operations
    :applyErrorState="applyErrorState && !showLoader"
    :errorStateMessage="t('accounts.extracts.errorStateMessage')"
    :showLoader="showLoader"
    :hideContentWrapper="hideContent"
    :showOverlayLoader="showOverlayLoader"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader"
    :activeStep="activeStep"
    :stepperParams="stepperParams"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="t('accounts.extracts.tokenTitleAppend')"
    :feedbackOperationData="feedbackOperationData"
    :scrollValue="scrollValue"
    @clearScrollToValue="scrollValue = null"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event.response.data, requestGenerateExtract)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *												 *
        *                     Content                    *
        *												 *
        ********************************************** -->
        <component
        v-if="!showLoader && !applyErrorState && !hideContent"
        :is="getActiveStepComponent"
        :extractData="extractData"
        :mandatoryList="mandatoryList"
        :showMandatoryErrors="showMandatoryErrors"
        :clearFormErrors="clearFormErrors"
        :operationInfoParams="operationInfoParams"
        @scrollTo="scrollValue = $event"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @formErrorsCleared="clearFormErrors = false"
        @extraDataChange="extractDataChangeHandler"
        @newExtract="newExtractClickHandler"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    :triggerClose="triggerClose"
    @hideMe="[showFeedbackToast = false, triggerClose = false]"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'
/* Aux */
import { clearObject, atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { createJSONDateEndDay } from '@/assets/scripts/dateBuilders'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { createMovementsExcelURL } from '@/network/api/accounts.api.js'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestAccountsBalances, generateExtract, clearFeatureStates } = mapActions(moduleMapper.ACCOUNTS_EXTRACTS)
const { getAccountsBalances } = mapGetters(moduleMapper.ACCOUNTS_EXTRACTS)
const { getMobileAppState } = mapGetters()

/* Generic Data */
const showLoader = ref(false)
const hideContent = ref(false)
const showOverlayLoader = ref(false)
const feedbackOperationData = ref(null)
const applyErrorState = ref(false)

/*************************************************
*                                                *
*                   Steps Data                   *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('accounts.extracts.stepper.data') },
    { description: t('accounts.extracts.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/accounts/extracts/Step' + stepNumber))
})

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const clearFn = () => {
    clearFormErrors.value = true
    extractData.value = clearObject(extractData.value)
    /* Reset Selected Account to Default */
    extractData.value.accountId = getAccountsBalances.value.find(account => { return account.default }).id
}

const validateInputs = () => {
    atLeastOneIsEmpty(extractData.value, mandatoryList.value) ? showMandatoryErrors.value = true : activeStep.value = 2
}

/* Footer Buttons Data */
const footerButtonsParams = ref([
    {
        label: t('accounts.extracts.footerButtons.clear'),
        callbackFunction: clearFn,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('accounts.extracts.footerButtons.forward'),
        callbackFunction: () => { validateInputs() },
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        step: 1
    }, {
        label: t('accounts.extracts.footerButtons.back'),
        callbackFunction: () => {
            activeStep.value = 1
            if (showFeedbackToast.value) triggerClose.value = true
        },
        buttonPosition: !getMobileAppState.value ? 'right' : 'left',
        icon: 'arrow-left-full',
        step: 2
    }, {
        label: t('accounts.extracts.footerButtons.validate'),
        callbackFunction: () => { requestGenerateExtract() },
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'check-line',
        iconPosition: 'right',
        step: 2
    }
])

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const extractData = ref({
    accountId: null,
    beginDate: null,
    endDate: null
})
/* We use extractData object keys as our mandatory array, becuz all keys are mandatory in this form */
const mandatoryList = ref(Object.keys(extractData.value))
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const extractDataChangeHandler = ({ field, inputValue }) => {
    extractData.value[field] = inputValue
}

/* Operation Info Data */
const operationInfoParams = ref({})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestAccountsBalance = async () => {
	showLoader.value = true
    applyErrorState.value = false
	showFeedbackToast.value = false
	cancelAllPendingRequests()

	try {
        await requestAccountsBalances()
        /* Setting Initial Selected Account */
        extractData.value.accountId = getAccountsBalances.value.find(account => { return account.default }).id
        showLoader.value = false
        hideContent.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response.data, requestAccountsBalance)
	}
}

const requestGenerateExtract = async () => {
    showOverlayLoader.value = true
	showFeedbackToast.value = false

    const payload = {
        accountId: extractData.value.accountId,
		params: {
			beginDate: extractData.value.beginDate,
			endDate: createJSONDateEndDay(extractData.value.endDate)
		}
    }

    try {
        await generateExtract(payload)
        showOverlayLoader.value = false
    } catch (error) {
		errorHandler(error.response.data, requestGenerateExtract)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())
const triggerClose = ref(false)

const challengeSuccessHandler = data => {
    operationInfoParams.value = {
        operationId: data.data.operationId,
        operationDate: data.data.creationDate,
        errorLevel: data.errorLevel
    }

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !data.data.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: operationInfoParams.value.operationId },
            multipleDownloadUrls: {
                downloadPDFUrl: createDownloadGenericReceiptPdf({ params: { operationID: operationInfoParams.value.operationId } }),
                downloadExcelUrl: createMovementsExcelURL({
                    accountId: extractData.value.accountId,
                    params: {
                        beginDate: extractData.value.beginDate,
                        endDate: createJSONDateEndDay(extractData.value.endDate)
                    }
                })
            },
            operationId: operationInfoParams.value.operationId
        }
    }

    goToStep(3)
}

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

const newExtractClickHandler = () => {
    hideContent.value = true

    setTimeout(() => {
        clearFn()
        requestAccountsBalance()
        feedbackOperationData.value = null
        activeStep.value = 1
    }, 0)
}

requestAccountsBalance()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
